import { PHYSICS, TILE } from '../config/config';
import type { TileMap } from './tilemap';

export type HitSide = 'top' | 'bottom' | 'left' | 'right';

export interface TileHit {
  tx: number;
  ty: number;
  side: HitSide;
}

/** An axis-aligned solid that is not part of the tile grid (moving platforms). */
export interface SolidBox {
  x: number;
  y: number;
  w: number;
  h: number;
  vx: number;
  vy: number;
  /** Only blocks from above (semi-solid). */
  oneWay?: boolean;
}

export interface Body {
  x: number;
  y: number;
  w: number;
  h: number;
  vx: number;
  vy: number;
  onGround: boolean;
  hitCeiling: boolean;
  hitLeft: boolean;
  hitRight: boolean;
  /** When true, one-way platforms are ignored entirely. */
  passOneWay?: boolean;
}

export interface MoveResult {
  hits: TileHit[];
  /** Platform the body landed on this move, if any. */
  riding: SolidBox | null;
  landed: boolean;
}

const EPS = 0.001;

/**
 * Swept, axis-separated tile collision.
 *
 * The movement for the step is split into sub-steps of at most PHYSICS.maxStep pixels
 * and each sub-step resolves X first and then Y. This is what prevents fast bodies from
 * tunnelling through thin geometry, and resolving the axes separately is what stops a
 * body from getting wedged in a wall when it slides along it.
 */
export function moveBody(
  b: Body,
  map: TileMap,
  dt: number,
  solids?: readonly SolidBox[],
): MoveResult {
  const res: MoveResult = { hits: [], riding: null, landed: false };
  b.hitCeiling = false;
  b.hitLeft = false;
  b.hitRight = false;
  b.onGround = false;

  const dx = b.vx * dt;
  const dy = b.vy * dt;
  const dist = Math.max(Math.abs(dx), Math.abs(dy));
  const steps = Math.max(1, Math.ceil(dist / PHYSICS.maxStep));
  const stepX = dx / steps;
  const stepY = dy / steps;

  for (let i = 0; i < steps; i++) {
    if (stepX !== 0) moveAxisX(b, map, stepX, res, solids);
    if (stepY !== 0) moveAxisY(b, map, stepY, res, solids);
  }

  // Ground probe: a body resting exactly on a tile edge with vy == 0 never performs a
  // downward sub-step, so confirm support explicitly instead of reporting "airborne".
  if (!res.landed && b.vy >= 0 && groundBelow(b, map, solids)) b.onGround = true;

  return res;
}

function groundBelow(b: Body, map: TileMap, solids?: readonly SolidBox[]): boolean {
  const y = b.y + b.h + 0.5;
  const x0 = Math.floor(b.x / TILE);
  const x1 = Math.floor((b.x + b.w - EPS) / TILE);
  const ty = Math.floor(y / TILE);
  for (let tx = x0; tx <= x1; tx++) {
    if (map.solid(tx, ty)) return true;
    if (!b.passOneWay && map.oneWay(tx, ty) && b.y + b.h <= ty * TILE + 1.5) return true;
  }
  if (solids) {
    for (const s of solids) {
      if (b.x < s.x + s.w && b.x + b.w > s.x && Math.abs(b.y + b.h - s.y) <= 1.5) return true;
    }
  }
  return false;
}

function moveAxisX(
  b: Body,
  map: TileMap,
  amount: number,
  res: MoveResult,
  solids?: readonly SolidBox[],
): void {
  b.x += amount;
  const y0 = Math.floor(b.y / TILE);
  const y1 = Math.floor((b.y + b.h - EPS) / TILE);

  if (amount > 0) {
    const tx = Math.floor((b.x + b.w - EPS) / TILE);
    for (let ty = y0; ty <= y1; ty++) {
      if (map.solid(tx, ty)) {
        b.x = tx * TILE - b.w;
        b.vx = 0;
        b.hitRight = true;
        res.hits.push({ tx, ty, side: 'right' });
        break;
      }
    }
  } else {
    const tx = Math.floor(b.x / TILE);
    for (let ty = y0; ty <= y1; ty++) {
      if (map.solid(tx, ty)) {
        b.x = (tx + 1) * TILE;
        b.vx = 0;
        b.hitLeft = true;
        res.hits.push({ tx, ty, side: 'left' });
        break;
      }
    }
  }

  if (solids) {
    for (const s of solids) {
      if (s.oneWay) continue;
      if (!boxOverlap(b, s)) continue;
      if (amount > 0) {
        b.x = s.x - b.w;
        b.hitRight = true;
      } else {
        b.x = s.x + s.w;
        b.hitLeft = true;
      }
      b.vx = 0;
    }
  }
}

function moveAxisY(
  b: Body,
  map: TileMap,
  amount: number,
  res: MoveResult,
  solids?: readonly SolidBox[],
): void {
  const prevBottom = b.y + b.h;
  b.y += amount;
  const x0 = Math.floor(b.x / TILE);
  const x1 = Math.floor((b.x + b.w - EPS) / TILE);

  if (amount >= 0) {
    const ty = Math.floor((b.y + b.h - EPS) / TILE);
    for (let tx = x0; tx <= x1; tx++) {
      const solid = map.solid(tx, ty);
      const oneWay =
        !solid && !b.passOneWay && map.oneWay(tx, ty) && prevBottom <= ty * TILE + 1.5;
      if (solid || oneWay) {
        b.y = ty * TILE - b.h;
        b.vy = 0;
        b.onGround = true;
        res.landed = true;
        res.hits.push({ tx, ty, side: 'top' });
        break;
      }
    }
  } else {
    const ty = Math.floor(b.y / TILE);
    for (let tx = x0; tx <= x1; tx++) {
      if (map.solid(tx, ty)) {
        b.y = (ty + 1) * TILE;
        b.vy = 0;
        b.hitCeiling = true;
        res.hits.push({ tx, ty, side: 'bottom' });
      }
    }
  }

  if (solids) {
    for (const s of solids) {
      if (!boxOverlap(b, s)) continue;
      if (amount >= 0) {
        // Landing on top of a platform: only when we were above it beforehand.
        if (prevBottom <= s.y + 2) {
          b.y = s.y - b.h;
          b.vy = 0;
          b.onGround = true;
          res.landed = true;
          res.riding = s;
        }
      } else if (!s.oneWay) {
        b.y = s.y + s.h;
        b.vy = 0;
        b.hitCeiling = true;
      }
    }
  }
}

function boxOverlap(b: Body, s: SolidBox): boolean {
  return (
    b.x < s.x + s.w - EPS && b.x + b.w > s.x + EPS && b.y < s.y + s.h - EPS && b.y + b.h > s.y + EPS
  );
}

/** Is there solid ground directly under this body? (ledge detection for enemies) */
export function groundAhead(map: TileMap, b: Body, dir: number, probe = 2): boolean {
  const px = dir > 0 ? b.x + b.w + probe : b.x - probe;
  const py = b.y + b.h + 2;
  return map.solidAtPixel(px, py) || map.oneWay(Math.floor(px / TILE), Math.floor(py / TILE));
}

/** True when a body of the given size fits at this position without touching solids. */
export function fits(map: TileMap, x: number, y: number, w: number, h: number): boolean {
  return !map.rectHitsSolid(x, y, w, h);
}

/**
 * Try to place a box of size w x h with its bottom-left at (x, bottom), nudging it up
 * to `maxLift` pixels upward if the head would be stuck inside a block.
 * Returns the resolved y, or null when no room exists.
 */
export function resolveGrow(
  map: TileMap,
  x: number,
  bottom: number,
  w: number,
  h: number,
  maxLift = TILE,
): number | null {
  for (let lift = 0; lift <= maxLift; lift++) {
    const y = bottom - h - lift;
    if (fits(map, x, y, w, h)) return y;
  }
  return null;
}
